// UK registrations and VINs typed into buyer messages, so the inbox can highlight them and
// pre-fill the fitment check. Current-style plates (AB16 CDE) are matched in any case because
// buyers often type them lower case; older prefix/suffix plates and VINs must be upper case,
// otherwise ordinary words and part numbers match too.
const CURRENT_PLATE = "[A-Z]{2}[0-9]{2} ?[A-Z]{3}";
const PREFIX_PLATE = "[A-HJ-PR-Y][0-9]{1,3} ?[A-Z]{3}";
const SUFFIX_PLATE = "[A-Z]{3} ?[0-9]{1,3}[A-HJ-PR-Y]";
const VIN = "[A-HJ-NPR-Z0-9]{17}";

const PATTERN = new RegExp(
  `\\b(?:(?<current>${CURRENT_PLATE})|(?<older>${PREFIX_PLATE}|${SUFFIX_PLATE})|(?<vin>${VIN}))\\b`,
  "gi"
);

export interface TextSegment {
  text: string;
  /** Normalised reg or VIN (upper case, no spaces) when this segment is one. */
  reg?: string;
  isVin?: boolean;
}

export function normaliseReg(reg: string) {
  return reg.replace(/\s+/g, "").toUpperCase();
}

/** Formats a normalised current-style plate as "AB16 CDE"; anything else is returned as-is. */
export function displayReg(reg: string) {
  const normalised = normaliseReg(reg);
  return /^[A-Z]{2}[0-9]{2}[A-Z]{3}$/.test(normalised)
    ? `${normalised.slice(0, 4)} ${normalised.slice(4)}`
    : normalised;
}

function isMatch(groups: Record<string, string | undefined>) {
  if (groups.current) return true;
  if (groups.older) return groups.older === groups.older.toUpperCase();
  if (groups.vin) {
    // A VIN always mixes letters and digits; 17 letters is a word, 17 digits a phone/order number.
    return groups.vin === groups.vin.toUpperCase() && /[0-9]/.test(groups.vin) && /[A-Z]/.test(groups.vin);
  }
  return false;
}

/** Splits text into plain and reg/VIN segments, in order, for rendering highlights. */
export function splitByRegs(text: string): TextSegment[] {
  const segments: TextSegment[] = [];
  let last = 0;

  for (const match of Array.from(text.matchAll(PATTERN))) {
    const groups = match.groups ?? {};
    if (!isMatch(groups)) continue;

    const start = match.index ?? 0;
    if (start > last) segments.push({ text: text.slice(last, start) });
    segments.push({ text: match[0], reg: normaliseReg(match[0]), isVin: !!groups.vin });
    last = start + match[0].length;
  }

  if (last < text.length) segments.push({ text: text.slice(last) });
  return segments;
}

/** Distinct normalised regs/VINs found in the text, in order of appearance. */
export function detectRegs(text: string): string[] {
  const regs = splitByRegs(text)
    .filter((s) => s.reg)
    .map((s) => s.reg as string);
  return Array.from(new Set(regs));
}
